/**
 * Storefront API client for the /collections page filter logic.
 */
(function () {
	let shopDomain = '';
	let apiToken = '';
	let apiVersion = '2025-04';
	let collectionHandle = 'all';
	let searchQuery = '';

	let discoverPromise = null;
	const matchCache = new Map();

	const COLLECTION_HANDLE_FALLBACKS = ['all-products', 'all-collections', 'shop-all'];

	/** Storefront routes that are not real Collection objects in the Storefront API. */
	const VIRTUAL_COLLECTION_HANDLES = new Set([
		'all',
		'all-products',
		'all-collections',
		'shop-all',
	]);

	const DEFAULT_STORE_SEARCH_QUERY = '*';

	const SEARCH_FILTERS_QUERY = `
		query SearchProductFilters {
			search(query: "*", types: PRODUCT, first: 1) {
				productFilters {
					id
					label
					type
					values {
						label
						count
						input
					}
				}
			}
		}
	`;

	function configure(opts) {
		if (!opts) return;
		if (opts.shopDomain) shopDomain = String(opts.shopDomain).trim();
		if (opts.apiToken) apiToken = String(opts.apiToken).trim();
		if (opts.apiVersion) apiVersion = String(opts.apiVersion).trim();
		if (opts.collectionHandle) collectionHandle = String(opts.collectionHandle).trim();
		if (opts.searchQuery != null) searchQuery = String(opts.searchQuery).trim();
		discoverPromise = null;
		matchCache.clear();
	}

	function isConfigured() {
		return Boolean(shopDomain && apiToken && apiVersion);
	}

	function endpoint() {
		return `https://${shopDomain}/api/${apiVersion}/graphql.json`;
	}

	async function gql(query, variables, signal) {
		if (!isConfigured()) throw new Error('Storefront API not configured');

		const res = await fetch(endpoint(), {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Accept: 'application/json',
				'X-Shopify-Storefront-Access-Token': apiToken,
			},
			body: JSON.stringify({ query, variables: variables || {} }),
			signal,
		});

		if (!res.ok) {
			throw new Error(`Storefront API HTTP ${res.status}`);
		}

		const payload = await res.json();
		if (payload.errors && payload.errors.length) {
			throw new Error(
				`Storefront API: ${payload.errors.map((e) => e.message).join('; ')}`
			);
		}
		return payload.data;
	}

	const DISCOVER_QUERY = `
		query DiscoverFilters($handle: String!) {
			collection(handle: $handle) {
				handle
				products(first: 250) {
					filters {
						id
						label
						type
						values {
							label
							count
							input
						}
					}
				}
			}
		}
	`;

	function normalizeFilterList(filters) {
		if (!Array.isArray(filters)) return [];
		return filters.filter((f) => f && f.id && Array.isArray(f.values) && f.values.length > 0);
	}

	async function discoverFiltersForHandle(handle, signal) {
		const data = await gql(DISCOVER_QUERY, { handle }, signal);
		const coll = data && data.collection;
		if (!coll || !coll.products) return { handle, filters: [] };
		return {
			handle: coll.handle || handle,
			filters: normalizeFilterList(coll.products.filters),
		};
	}

	async function discoverFiltersFromStorefront({ signal } = {}) {
		if (!isConfigured()) throw new Error('Storefront API not configured');

		try {
			const data = await gql(SEARCH_FILTERS_QUERY, {}, signal);
			const filters = normalizeFilterList(
				data && data.search && data.search.productFilters
			);
			if (filters.length > 0) return filters;
		} catch (err) {
			if (err && err.name === 'AbortError') throw err;
		}

		const handlesToTry = [
			collectionHandle,
			...COLLECTION_HANDLE_FALLBACKS.filter((h) => h !== collectionHandle),
		]
			.filter((h) => h && !VIRTUAL_COLLECTION_HANDLES.has(h));
		const seen = new Set();

		for (const handle of handlesToTry) {
			if (!handle || seen.has(handle)) continue;
			seen.add(handle);
			try {
				const result = await discoverFiltersForHandle(handle, signal);
				if (result.filters.length > 0) {
					if (result.handle && result.handle !== collectionHandle) {
						collectionHandle = result.handle;
					}
					return result.filters;
				}
			} catch (err) {
				if (err && err.name === 'AbortError') throw err;
			}
		}

		return [];
	}

	async function discoverFilters({ signal } = {}) {
		if (!discoverPromise) {
			discoverPromise = discoverFiltersFromStorefront({ signal }).catch((err) => {
				discoverPromise = null;
				throw err;
			});
		}
		return discoverPromise;
	}

	/**
	 * Parse filter groups from a Section Rendering response (main-collection-product-grid on /collections/all).
	 */
	function parseFiltersFromFacetHtml(html) {
		const doc = new DOMParser().parseFromString(html, 'text/html');
		const filters = [];
		const seenParams = new Set();

		const detailsNodes = doc.querySelectorAll(
			[
				'details.facets__disclosure[data-filter-param]',
				'details.js-filter[data-filter-param]',
				'details.facets__disclosure.js-filter',
				'details.mobile-facets__details.js-filter',
				'#FacetsWrapperDesktop details.js-filter',
				'.facets__wrapper details.js-filter',
			].join(', ')
		);

		detailsNodes.forEach((details) => {
			const firstCheckbox = details.querySelector(
				'input[type="checkbox"][name^="filter."]'
			);
			const param =
				details.getAttribute('data-filter-param') ||
				(firstCheckbox && firstCheckbox.getAttribute('name'));
			if (!param || !param.startsWith('filter.') || seenParams.has(param)) return;

			const labelEl = details.querySelector('.facets__summary-title');
			let label = param;
			if (labelEl) {
				const clone = labelEl.cloneNode(true);
				clone.querySelectorAll('svg, .facets__arrow').forEach((el) => el.remove());
				label = (clone.textContent || param).trim();
			}

			const values = [];
			const valueSeen = new Set();

			details.querySelectorAll('input[type="checkbox"][name^="filter."]').forEach((input) => {
				const value = String(input.value || '').trim();
				if (!value || valueSeen.has(value)) return;
				valueSeen.add(value);

				let displayLabel = value;
				const labelSpan = input.closest('li')?.querySelector('.facet-checkbox__label');
				if (labelSpan) {
					displayLabel = (labelSpan.textContent || value).trim();
				}

				let inputJson = null;
				if (window.ClistStorefrontAPI && typeof window.ClistStorefrontAPI.coerceProductFilter === 'function') {
					const built = window.ClistStorefrontAPI.coerceProductFilter(param, value, null);
					if (built) inputJson = JSON.stringify(built);
				} else if (
					window.ClistStorefrontAPI &&
					typeof window.ClistStorefrontAPI.buildProductFilter === 'function'
				) {
					const built = window.ClistStorefrontAPI.buildProductFilter(param, value);
					if (built) inputJson = JSON.stringify(built);
				}

				values.push({
					label: displayLabel,
					value,
					count: 1,
					input: inputJson,
				});
			});

			if (values.length === 0) return;
			seenParams.add(param);
			filters.push({
				id: param,
				label,
				type: 'LIST',
				values,
			});
		});

		return filters;
	}

	async function discoverFiltersFromSection({ collectionPath, sectionIds, signal } = {}) {
		const path = collectionPath || '/collections/all';
		const ids = Array.isArray(sectionIds) ? sectionIds : [sectionIds];
		const uniqueIds = [...new Set(ids.filter(Boolean))];

		for (const sectionId of uniqueIds) {
			try {
				const url = new URL(path, window.location.origin);
				url.searchParams.set('section_id', sectionId);
				const res = await fetch(url.toString(), {
					credentials: 'same-origin',
					signal,
				});
				if (!res.ok) continue;
				const filters = parseFiltersFromFacetHtml(await res.text());
				if (filters.length > 0) return filters;
			} catch (err) {
				if (err && err.name === 'AbortError') throw err;
			}
		}

		return [];
	}

	/**
	 * Native Liquid JSON → Section Rendering API (/collections/all facets) → Storefront API.
	 * Section rendering is tried before Storefront API because list-collections templates
	 * often cannot read collection.filters in Liquid, while the PLP section can.
	 */
	async function discoverFiltersWithFallback(options = {}) {
		const { nativeFilters, collectionPath, sectionIds, signal } = options;
		const native = normalizeFilterList(nativeFilters);
		if (native.length > 0) return native;

		const fromSection = await discoverFiltersFromSection({
			collectionPath,
			sectionIds,
			signal,
		});
		if (fromSection.length > 0) return fromSection;

		if (isConfigured()) {
			try {
				const fromApi = await discoverFilters({ signal });
				if (fromApi.length > 0) return fromApi;
			} catch (err) {
				if (err && err.name === 'AbortError') throw err;
				console.warn('[ClistStorefrontAPI] discoverFilters failed:', err);
			}
		}

		return [];
	}

	const SEARCH_MATCH_QUERY = `
		query MatchingCollectionsSearch(
			$query: String!
			$filters: [ProductFilter!]
			$cursor: String
		) {
			search(query: $query, productFilters: $filters, types: PRODUCT, first: 250, after: $cursor) {
				edges {
					node {
						... on Product {
							collections(first: 50) {
								nodes { handle }
							}
						}
					}
				}
				pageInfo {
					hasNextPage
					endCursor
				}
			}
		}
	`;

	const COLLECTION_PRODUCTS_MATCH_QUERY = `
		query MatchingCollectionsFromCollection(
			$handle: String!
			$cursor: String
		) {
			collectionByHandle(handle: $handle) {
				products(first: 250, after: $cursor) {
					edges {
						node {
							... on Product {
								collections(first: 50) {
									nodes { handle }
								}
							}
						}
					}
					pageInfo {
						hasNextPage
						endCursor
					}
				}
			}
		}
	`;

	function resolveStoreSearchQuery() {
		const custom = String(searchQuery || '').trim();
		if (custom) return custom;

		/*
			When no explicit search query is provided (e.g. collection pages),
			scope Storefront "search → collections" matching to the active collection.
			This prevents Collections tab from listing every collection by default.
		*/
		const handle = String(collectionHandle || '').trim();
		if (handle && !VIRTUAL_COLLECTION_HANDLES.has(handle)) {
			return `collection:${handle}`;
		}

		return DEFAULT_STORE_SEARCH_QUERY;
	}

	function collectHandlesFromSearchEdges(edges, handles) {
		for (const edge of edges || []) {
			const node = edge && edge.node;
			const colls = node && node.collections && node.collections.nodes;
			if (!Array.isArray(colls)) continue;
			for (const c of colls) {
				if (c && c.handle) handles.add(c.handle);
			}
		}
	}

	function normalizeFilters(filters) {
		return [...filters]
			.map((f) => JSON.stringify(f))
			.sort()
			.map((s) => JSON.parse(s));
	}

	async function queryMatchingCollections({ filters, signal, maxPages, searchQuery: queryOverride } = {}) {
		if (!isConfigured()) throw new Error('Storefront API not configured');

		const filtersArr = Array.isArray(filters) ? normalizeFilters(filters) : [];
		const explicitQuery =
			queryOverride != null && String(queryOverride).trim() !== ''
				? String(queryOverride).trim()
				: '';
		const storeQuery = explicitQuery || resolveStoreSearchQuery();
		const cacheKey = JSON.stringify({ filters: filtersArr, q: storeQuery });
		if (matchCache.has(cacheKey)) {
			return matchCache.get(cacheKey);
		}

		const handles = new Set();
		let cursor = null;
		const limit = Number.isInteger(maxPages) && maxPages > 0 ? maxPages : 25;
		let pageCount = 0;

		// Collection pages: if no explicit searchQuery is provided, scope matching via collectionByHandle
		// to avoid relying on Storefront search query syntax.
		const implicitHandle =
			!String(searchQuery || '').trim() &&
			String(collectionHandle || '').trim() &&
			!VIRTUAL_COLLECTION_HANDLES.has(String(collectionHandle || '').trim())
				? String(collectionHandle || '').trim()
				: '';

		while (pageCount < limit) {
			pageCount += 1;
			if (implicitHandle) {
				const data = await gql(
					COLLECTION_PRODUCTS_MATCH_QUERY,
					{ handle: implicitHandle, cursor },
					signal
				);
				const products = data?.collectionByHandle?.products;
				const edges = products?.edges || [];
				collectHandlesFromSearchEdges(edges, handles);
				const pageInfo = products?.pageInfo;
				if (!pageInfo || !pageInfo.hasNextPage) break;
				cursor = pageInfo.endCursor;
				continue;
			}

			const data = await gql(
				SEARCH_MATCH_QUERY,
				{ query: storeQuery, filters: filtersArr, cursor },
				signal
			);
			const search = data && data.search;
			if (!search) break;

			collectHandlesFromSearchEdges(search.edges, handles);

			const pageInfo = search.pageInfo;
			if (!pageInfo || !pageInfo.hasNextPage) break;
			cursor = pageInfo.endCursor;
		}

		matchCache.set(cacheKey, handles);
		return handles;
	}

	function buildProductFilter(storefrontParam, value) {
		if (!storefrontParam) return null;

		let m = storefrontParam.match(/^filter\.p\.m\.([^.]+)\.(.+)$/);
		if (m) {
			return { productMetafield: { namespace: m[1], key: m[2], value } };
		}

		m = storefrontParam.match(/^filter\.v\.m\.([^.]+)\.(.+)$/);
		if (m) {
			return { variantMetafield: { namespace: m[1], key: m[2], value } };
		}

		if (storefrontParam === 'filter.p.product_type') return { productType: value };
		if (storefrontParam === 'filter.p.vendor') return { productVendor: value };
		if (storefrontParam === 'filter.p.tag') return { tag: value };

		m = storefrontParam.match(/^filter\.v\.option\.(.+)$/);
		if (m) {
			return { variantOption: { name: m[1], value } };
		}

		if (storefrontParam === 'filter.v.availability') {
			return { available: value === '1' || value === 'true' };
		}

		return null;
	}

	/** Variant metafield filters — must use variantMetafield in ProductFilter, not productMetafield. */
	const VARIANT_METAFIELD_FILTER_KEYS = new Set([
		'size_small_filter',
		'size_large_filter',
		'size_x_large_filter',
		'wall_tile_specifics_filter',
		'mosaic_specifics_filter',
		'trim_specifics_filter',
	]);

	function isVariantMetafieldStorefrontParam(storefrontParam) {
		if (String(storefrontParam || '').startsWith('filter.v.m.')) return true;
		const m = String(storefrontParam || '').match(/^filter\.(p|v)\.m\.([^.]+)\.(.+)$/);
		return Boolean(m && VARIANT_METAFIELD_FILTER_KEYS.has(m[3]));
	}

	/**
	 * Build ProductFilter from the URL param prefix (authoritative).
	 * Discovery `input` JSON may incorrectly use productMetafield for variant filters.
	 */
	function coerceProductFilter(storefrontParam, value, rawInput) {
		const fromParam = buildProductFilter(storefrontParam, value);
		if (fromParam) return fromParam;

		if (!rawInput || typeof rawInput !== 'object') return null;

		if (rawInput.variantMetafield || rawInput.productMetafield) {
			const mf = rawInput.variantMetafield || rawInput.productMetafield;
			const ns = mf.namespace;
			const key = mf.key;
			const mfValue = mf.value != null ? mf.value : value;
			if (ns && key) {
				if (isVariantMetafieldStorefrontParam(`filter.v.m.${ns}.${key}`) || rawInput.variantMetafield) {
					return { variantMetafield: { namespace: ns, key, value: mfValue } };
				}
				return { productMetafield: { namespace: ns, key, value: mfValue } };
			}
		}

		return rawInput;
	}

	function clearCaches() {
		discoverPromise = null;
		matchCache.clear();
	}

	window.ClistStorefrontAPI = {
		configure,
		isConfigured,
		discoverFilters,
		discoverFiltersFromSection,
		discoverFiltersWithFallback,
		parseFiltersFromFacetHtml,
		queryMatchingCollections,
		buildProductFilter,
		coerceProductFilter,
		isVariantMetafieldStorefrontParam,
		clearCaches,
	};
})();
